// English view texts. Keys are the German source strings passed to t()/tk();
// `{name}` placeholders must be kept. `npm test` (test/i18n.test.ts) fails when
// a key is missing here or a translation drops a placeholder.

export const EN_TEXT: Record<string, string> = {
  // ------------------------------------------------------------ shell and common
  'Übersicht': 'Overview',
  'Projekte': 'Projects',
  'Kalender': 'Calendar',
  'Kontakte': 'Contacts',
  'Firmen': 'Companies',
  'Verlauf': 'Activity',
  'Einstellungen': 'Settings',
  'Setup': 'Setup',
  'Zum Inhalt': 'Skip to content',
  'Hauptnavigation': 'Main navigation',
  'Mobile Hauptnavigation': 'Mobile main navigation',
  'Organisation': 'Organisation',
  'Suchen': 'Search',
  'Suchen … (Projekte, Aufgaben, Risiken, Kontakte)': 'Search … (projects, tasks, risks, contacts)',
  'Benachrichtigungen': 'Notifications',
  'Benachrichtigungen, {n} ungelesen': 'Notifications, {n} unread',
  'Alle gelesen': 'Mark all read',
  'Keine Benachrichtigungen. Erwähnungen, Zuweisungen und Kommentare zu deinen Aufgaben erscheinen hier.':
    'No notifications. Mentions, assignments and comments on your tasks appear here.',
  'Dunkles Design': 'Dark theme',
  'Profil': 'Profile',
  'Abmelden': 'Sign out',
  'Keine Treffer.': 'No results.',
  'Seite nicht gefunden': 'Page not found',
  'Zur Übersicht': 'Back to overview',
  'Aktion fehlgeschlagen': 'Action failed',
  'Wird geladen …': 'Loading …',
  'Abbrechen': 'Cancel',
  'Speichern': 'Save',
  'Löschen': 'Delete',
  'Bearbeiten': 'Edit',
  'Schließen': 'Close',
  'Anlegen': 'Create',
  'Erstellen': 'Create',
  'Hinzufügen': 'Add',
  'Entfernen': 'Remove',
  'Kopieren': 'Copy',
  'Kopiert, schließen': 'Copied, close',
  'Integration': 'Integration',
  'Connector': 'Connector',
  'System': 'System',
  'Unbekannt': 'Unknown',
  'Name': 'Name',
  'Titel': 'Title',
  'Status': 'Status',
  'Beschreibung': 'Description',
  'Notizen': 'Notes',
  'Art': 'Type',
  'Start': 'Start',
  'Ende': 'End',
  'Budget': 'Budget',
  'Ist': 'Actual',
  'Fällig': 'Due',
  'fällig': 'due',
  'Phase': 'Phase',
  'Keine Phase': 'No phase',
  'Niemand': 'Nobody',
  'Keine': 'None',
  'Bitte wählen': 'Please choose',
  'Offen': 'Open',
  'Aktiv': 'Active',
  'Pausiert': 'Paused',
  'Pausieren': 'Pause',
  'Aktivieren': 'Activate',
  'Automatisch': 'Automatic',
  'automatisch': 'automatic',
  'Verantwortlich': 'Owner',
  'Projekt': 'Project',
  'Kunde': 'Client',
  'Kontakt': 'Contact',
  'Firma': 'Company',
  'Aufgabe': 'Task',
  'Aufgaben': 'Tasks',
  'Risiko': 'Risk',
  'Risiken': 'Risks',
  'Entscheidung': 'Decision',
  'Entscheidungen': 'Decisions',
  'Gate': 'Gate',
  'Gates': 'Gates',
  'Phasen': 'Phases',
  'Stakeholder': 'Stakeholders',
  'Kennzahlen': 'KPIs',
  'Priorität': 'Priority',
  'Zuständig': 'Assignee',
  'Fehler': 'Error',
  'Ziel': 'Target',
  'Zielwert': 'Target value',
  'zuletzt {when}': 'last {when}',
  'von {name}': 'by {name}',
  'Von': 'From',
  'Bis': 'To',
  '{name} bearbeiten': 'Edit {name}',
  '{key} bearbeiten': 'Edit {key}',
  '„{title}“ wird endgültig gelöscht.': '“{title}” will be deleted permanently.',
  'Keine Treffer': 'No matches',
  'https://…': 'https://…',
  'https://… (optional)': 'https://… (optional)',
  'Link': 'Link',

  // ------------------------------------------------------------ activity and signals
  '{actor} hat {entity} {label} {action}': '{actor} {action} {entity} {label}',
  'Felder': 'Fields',
  'Noch keine Aktivität': 'No activity yet',
  'Änderungen erscheinen hier automatisch – als Verlauf, Audit-Log und Quelle für Integrationen.':
    'Changes appear here automatically – as history, audit log and source for integrations.',
  'Kritisch': 'Critical',
  'Achtung': 'Warning',
  'Hinweis': 'Note',
  'Hinweise': 'Signals',
  'Keine Hinweise': 'No signals',
  'Termine, Gates, Risiken und Abwesenheiten sind aktuell unauffällig.': 'Deadlines, gates, risks and absences look fine right now.',
  'regelbasiert · mit Begründung': 'rule-based · with reasons',
  'Audit': 'Audit',
  'Jede Änderung in der Organisation – unveränderlich protokolliert und Quelle für Webhooks.':
    'Every change in the organisation – logged immutably and the source for webhooks.',

  // ------------------------------------------------------------ sign-in
  'Projekte, Risiken, Entscheidungen, Gates und Menschen an einem Ort – verknüpft, nachvollziehbar und offen für deine Werkzeuge.':
    'Projects, risks, decisions, gates and people in one place – connected, traceable and open to your tools.',
  'RAID-Register mit Risikomatrix und Entscheidungsprotokoll': 'RAID register with risk matrix and decision log',
  'Phasen und Freigabe-Gates mit messbaren Kriterien': 'Phases and approval gates with measurable criteria',
  'API, signierte Webhooks und Audit-Log ab dem ersten Tag': 'API, signed webhooks and audit log from day one',
  'Arbeitsbereich anlegen': 'Create workspace',
  'Anmelden': 'Sign in',
  'Dein Name': 'Your name',
  'Deine Firma oder dein Freelance-Name': 'Your company or freelance name',
  'E-Mail': 'Email',
  'Passwort': 'Password',
  'Mindestens 10 Zeichen': 'At least 10 characters',
  'Konto erstellen': 'Create account',
  'Schon registriert?': 'Already registered?',
  'Noch kein Konto?': 'No account yet?',

  // ------------------------------------------------------------ dashboard
  'Guten Tag, {name}.': 'Hello, {name}.',
  'Guten Tag.': 'Hello.',
  'Was heute Aufmerksamkeit braucht – über alle Projekte.': 'What needs attention today – across all projects.',
  'Aktive Projekte': 'Active projects',
  'Offene Aufgaben': 'Open tasks',
  'Überfällig': 'Overdue',
  'Kritische Risiken': 'Critical risks',
  'Gates in 14 Tagen': 'Gates in 14 days',
  'Offene Entscheidungen': 'Open decisions',
  'Hinweise des Assistenten': 'Assistant signals',
  'Nächste Aufgaben': 'Next tasks',
  'Alles erledigt': 'All done',
  'Keine offenen Aufgaben für dich.': 'No open tasks for you.',
  'Anstehende Gates': 'Upcoming gates',
  '{met}/{total} Kriterien': '{met}/{total} criteria',
  'Keine offenen Gates': 'No open gates',
  'Projekte mit Vorlage erzeugen Gates automatisch.': 'Projects created from a template get gates automatically.',
  'Top-Risiken': 'Top risks',
  'Keine offenen Risiken': 'No open risks',
  'Erfasse Risiken im Projekt, um sie hier zu sehen.': 'Record risks in a project to see them here.',
  'Letzte Aktivität': 'Recent activity',
  'Score {n}': 'Score {n}',

  // ------------------------------------------------------------ projects
  'Portfolio': 'Portfolio',
  'Archivierte Projekte': 'Archived projects',
  'Status, Ampel und Aufmerksamkeitspunkte aller Projekte.': 'Status, health and attention points of all projects.',
  'Aktive anzeigen': 'Show active',
  'Archiv': 'Archive',
  'Neues Projekt': 'New project',
  'Keine archivierten Projekte': 'No archived projects',
  'Noch keine Projekte': 'No projects yet',
  'Archivierte Projekte erscheinen hier.': 'Archived projects appear here.',
  'Lege dein erstes Projekt an – mit Vorlage entstehen Phasen, Gates und Startaufgaben automatisch.':
    'Create your first project – a template sets up phases, gates and starter tasks automatically.',
  'Ampel': 'Health',
  'Nächstes Gate': 'Next gate',
  '{n} überfällig': '{n} overdue',
  'max {n}': 'max {n}',
  'Automatisch abgeleitet aus Terminen, Risiken und Gates': 'Derived automatically from deadlines, risks and gates',
  'Kürzel': 'Key',
  '2–10 Großbuchstaben/Ziffern, z. B. PMA': '2–10 capital letters/digits, e.g. PMA',
  'Kein Kunde': 'No client',
  'Eine Vorlage legt Phasen, Gates mit Kriterien, Startaufgaben und Kennzahlen an.':
    'A template creates phases, gates with criteria, starter tasks and KPIs.',
  'Vorlage': 'Template',
  '{n} W': '{n} wk',
  'Projekt anlegen': 'Create project',
  'Projekt angelegt': 'Project created',
  'Projekt gespeichert': 'Project saved',

  // ------------------------------------------------------------ project view
  'Überblick': 'Overview',
  'Zeitplan': 'Schedule',
  'Phasen & Gates': 'Phases & gates',
  'Statusbericht': 'Status report',
  'Wiederherstellen': 'Restore',
  'Archivieren': 'Archive',
  'Projektstatus': 'Project status',
  'Offene Risiken': 'Open risks',
  'Offene Gates': 'Open gates',
  'nächstes {date}': 'next {date}',
  'Projektbereiche': 'Project sections',
  'Keine Beschreibung.': 'No description.',
  'Stand': 'Status',
  'Aktuelle Phase': 'Current phase',
  'bis {date}': 'until {date}',
  'Freigabe für Gäste': 'Guest access',
  'Freigabe entziehen': 'Revoke access',
  'Nicht freigegeben. Gäste (z. B. Kunden) sehen dieses Projekt erst nach einer Freigabe – nur lesend, ohne Kontakte und interne Projekte.':
    'Not shared. Guests (such as clients) only see this project once it is shared with them – read-only, without contacts or internal projects.',
  'Gast auswählen': 'Choose guest',
  'Freigeben': 'Share',
  'Gäste fügst du unter Einstellungen → Organisation hinzu.': 'Add guests under Settings → Organisation.',
  'Projekt archiviert': 'Project archived',
  'Projekt wiederhergestellt': 'Project restored',
  'Projekt löschen?': 'Delete project?',
  '{project} wird mit allen Aufgaben, Risiken, Entscheidungen und Notizen endgültig gelöscht. Archivieren ist meist die bessere Wahl.':
    '{project} will be deleted permanently with all tasks, risks, decisions and notes. Archiving is usually the better choice.',
  'Projekt gelöscht': 'Project deleted',
  'Automatisch aus Aufgaben, Gates, Risiken, Entscheidungen und Kennzahlen erzeugt (Markdown).':
    'Generated automatically from tasks, gates, risks, decisions and KPIs (Markdown).',
  'Bericht der letzten {n} Tage': 'Report for the last {n} days',
  'Als .md herunterladen': 'Download as .md',
  'In Zwischenablage kopieren': 'Copy to clipboard',
  'statusbericht': 'status-report',
  'Bericht kopiert': 'Report copied',
  'Drucken / PDF': 'Print / PDF',
  'Das Druckfenster wurde vom Browser blockiert.': 'The browser blocked the print window.',
  'Erstellt am {date}': 'Created on {date}',
  'Zusammenfassung schreiben (Assistent)': 'Write summary (assistant)',
  'Der Assistent fasst nur den Bericht zusammen; der Text landet oben im Bericht und kann vor dem Teilen bearbeitet werden.':
    'The assistant only summarises the report; the text goes to the top of the report and can be edited before sharing.',
  'Export': 'Export',
  'Als CSV exportieren': 'Export as CSV',
  'Für Tabellenkalkulationen und Übergaben. Spalten und Werte bleiben maschinenlesbar (englische Spaltennamen, Rohwerte).':
    'For spreadsheets and hand-offs. Columns and values stay machine-readable (English column names, raw values).',
  'Register': 'Register',
  'Trennzeichen': 'Delimiter',
  'Semikolon (Excel, deutsch)': 'Semicolon (Excel, German)',
  'Komma (international)': 'Comma (international)',
  'Herunterladen': 'Download',

  // ------------------------------------------------------------ tasks
  'Neue Aufgabe': 'New task',
  'Delegiert an Kontakt': 'Delegated to contact',
  'Nicht delegiert': 'Not delegated',
  'Aufgabe gespeichert': 'Task saved',
  'Aufgabe angelegt': 'Task created',
  'Aufgabe löschen?': 'Delete task?',
  '{key} „{title}“ wird endgültig gelöscht.': '{key} “{title}” will be deleted permanently.',
  'Ansicht': 'View',
  'Board': 'Board',
  'Liste': 'List',
  'Alle Phasen': 'All phases',
  '{open} offen · {total} gesamt': '{open} open · {total} total',
  'Enter öffnet die Aufgabe. Alt und Pfeiltasten verschieben sie zwischen und innerhalb der Spalten.':
    'Enter opens the task. Alt and the arrow keys move it between and within columns.',
  'Noch keine Aufgaben': 'No tasks yet',
  'Lege die erste Aufgabe an oder nutze eine Projektvorlage.': 'Create the first task or use a project template.',
  'Nr.': 'No.',
  '{key} in {status}, Position {n}': '{key} in {status}, position {n}',

  // ------------------------------------------------------------ risks
  'Kategorie': 'Category',
  'Wahrscheinlichkeit': 'Probability',
  'Auswirkung': 'Impact',
  'Strategie': 'Response',
  'Noch offen': 'Not decided',
  'Nächste Prüfung': 'Next review',
  'Verantwortlich (Team)': 'Owner (team)',
  'Verantwortlich (Kontakt)': 'Owner (contact)',
  'Maßnahmen': 'Mitigation',
  'Maßnahme': 'Mitigation',
  'Risikomatrix': 'Risk matrix',
  'Offene und in Behandlung befindliche Risiken · Zelle wählen zum Filtern': 'Open and mitigating risks · choose a cell to filter',
  'Anzahl Risiken nach Wahrscheinlichkeit (Zeilen) und Auswirkung (Spalten)': 'Number of risks by probability (rows) and impact (columns)',
  'Wahrscheinlichkeit {p}, Auswirkung {i}: {n} Risiken': 'Probability {p}, impact {i}: {n} risks',
  '↑ Wahrscheinlichkeit': '↑ Probability',
  'Auswirkung →': 'Impact →',
  'Geschlossene zeigen': 'Show closed',
  'Filter {cell} aufheben': 'Clear filter {cell}',
  'Neues Risiko': 'New risk',
  'Prüfung {date}': 'review {date}',
  'Keine Risiken erfasst': 'No risks recorded',
  'Filter anpassen, um weitere Risiken zu sehen.': 'Adjust the filter to see more risks.',
  'Bewerte Wahrscheinlichkeit und Auswirkung von 1 bis 5 – der Score entsteht automatisch.':
    'Rate probability and impact from 1 to 5 – the score is calculated automatically.',
  'Risiko bearbeiten': 'Edit risk',
  'Risiko gespeichert': 'Risk saved',
  'Risiko erfasst': 'Risk recorded',
  'Risiko löschen?': 'Delete risk?',

  // ------------------------------------------------------------ decisions, notes, stakeholders, KPIs
  'Neue Entscheidung': 'New decision',
  '{n} Einträge · {open} offen': '{n} entries · {open} open',
  'Entschieden am {date}': 'Decided on {date}',
  'Erfasst am {date}': 'Recorded on {date}',
  'Kontext': 'Context',
  'Ergebnis': 'Outcome',
  'Noch keine Entscheidungen': 'No decisions yet',
  'Das Entscheidungsprotokoll hält fest, was warum beschlossen wurde. Gate-Entscheidungen landen hier automatisch.':
    'The decision log records what was decided and why. Gate decisions land here automatically.',
  'Entscheidung bearbeiten': 'Edit decision',
  'Entschieden am': 'Decided on',
  'Entschieden von': 'Decided by',
  'Kontext und Optionen': 'Context and options',
  'Ergebnis und Begründung': 'Outcome and rationale',
  'Entscheidung gespeichert': 'Decision saved',
  'Entscheidung löschen?': 'Delete decision?',
  '„{title}“ wird gelöscht. Der Verlauf bleibt im Audit-Log erhalten.': '“{title}” will be deleted. Its history stays in the audit log.',
  'Neue Notiz': 'New note',
  '{n} Notizen': '{n} notes',
  'Vorschläge ableiten': 'Suggest items',
  'am {date}': 'on {date}',
  'Version {n}': 'version {n}',
  'Leer.': 'Empty.',
  'Noch keine Notizen': 'No notes yet',
  'Halte Besprechungen, Beobachtungen und Lessons Learned fest – jede Änderung wird versioniert.':
    'Capture meetings, observations and lessons learned – every change is versioned.',
  'Notiz bearbeiten': 'Edit note',
  'Besprechung am': 'Meeting on',
  'Inhalt': 'Content',
  'Notiz gespeichert': 'Note saved',
  'Notiz löschen?': 'Delete note?',
  '„{title}“ und alle Versionen werden gelöscht.': '“{title}” and all its versions will be deleted.',
  'Assistent arbeitet …': 'Assistant is working …',
  'Vorschläge aus der Notiz': 'Suggestions from the note',
  'Erzeugt von {provider}. Nichts wird angelegt, bevor du auswählst und übernimmst. Titel lassen sich anpassen.':
    'Generated by {provider}. Nothing is created until you select and apply. Titles can be edited.',
  'Der Assistent ({provider}) hat keine Aufgaben, Entscheidungen oder Risiken gefunden.':
    'The assistant ({provider}) found no tasks, decisions or risks.',
  'fällig {date}': 'due {date}',
  'Wahrscheinlichkeit {p} × Auswirkung {i}': 'probability {p} × impact {i}',
  'Auswahl übernehmen': 'Apply selection',
  '{n} Einträge übernommen': '{n} items created',
  'Aufgaben, Entscheidungen und Risiken findest du in den jeweiligen Tabs.': 'You find tasks, decisions and risks in their tabs.',
  'Eng einbinden': 'Manage closely',
  'Zufriedenstellen': 'Keep satisfied',
  'Informieren': 'Keep informed',
  'Beobachten': 'Monitor',
  'Stakeholder hinzufügen': 'Add stakeholder',
  '{n} Stakeholder': '{n} stakeholders',
  'Stakeholder nach Einfluss und Interesse': 'Stakeholders by influence and interest',
  '↑ Einfluss · Interesse →': '↑ Influence · Interest →',
  'Noch keine Stakeholder': 'No stakeholders yet',
  'Ordne Kontakte dem Projekt zu und bewerte Einfluss und Interesse.': 'Assign contacts to the project and rate their influence and interest.',
  'Lege zuerst Kontakte an und ordne sie dann dem Projekt zu.': 'Create contacts first, then assign them to the project.',
  'sehr gering': 'very low',
  'gering': 'low',
  'mittel': 'medium',
  'hoch': 'high',
  'sehr hoch': 'very high',
  'Stakeholder bearbeiten': 'Edit stakeholder',
  'Rolle im Projekt': 'Role in the project',
  'Einfluss': 'Influence',
  'Interesse': 'Interest',
  'RACI': 'RACI',
  'Durchführung': 'Responsible',
  'Verantwortung': 'Accountable',
  'Konsultiert': 'Consulted',
  'Informiert': 'Informed',
  'Stakeholder entfernen?': 'Remove stakeholder?',
  'Der Kontakt bleibt erhalten, nur die Projektzuordnung wird entfernt.': 'The contact is kept; only the project assignment is removed.',
  'Neue Kennzahl': 'New KPI',
  '{n} Kennzahlen': '{n} KPIs',
  'Kein Zielwert': 'No target',
  'Neuer Messwert für {name}': 'New measurement for {name}',
  'Wert': 'Value',
  'Messdatum': 'Measurement date',
  'Erfassen': 'Record',
  'Keine Kennzahlen': 'No KPIs',
  'Definiere messbare Ziele wie Erfolgsquote oder Kosten pro Fall. Werte kommen manuell oder per API.':
    'Define measurable goals such as success rate or cost per case. Values come in manually or via the API.',
  'Kennzahl bearbeiten': 'Edit KPI',
  'Einheit': 'Unit',
  '%, EUR, Fälle': '%, EUR, cases',
  'Besser ist': 'Better is',
  'Höher': 'Higher',
  'Niedriger': 'Lower',
  'Kennzahl löschen?': 'Delete KPI?',
  '„{name}“ und alle Messwerte werden gelöscht.': '“{name}” and all its measurements will be deleted.',

  // ------------------------------------------------------------ phases and gates
  'Ist-Wert': 'Actual value',
  'Ist-Wert für {label}': 'Actual value for {label}',
  'Bewertung für {label}': 'Assessment for {label}',
  'Erfüllt': 'Met',
  'Nicht erfüllt': 'Not met',
  '{met} von {total} Kriterien erfüllt': '{met} of {total} criteria met',
  'Keine Kriterien': 'No criteria',
  'Erfüllte Kriterien': 'Criteria met',
  'Kriterium hinzufügen': 'Add criterion',
  'Neues Kriterium': 'New criterion',
  'Ziel (optional)': 'Target (optional)',
  'Bestanden': 'Passed',
  'Mit Ausnahme freigeben': 'Approve with waiver',
  'Nicht bestanden': 'Failed',
  'Entschieden am {date} · im Entscheidungsprotokoll dokumentiert': 'Decided on {date} · recorded in the decision log',
  '{phases} Phasen · {gates} offene Gates': '{phases} phases · {gates} open gates',
  'Neue Phase': 'New phase',
  'Neues Gate': 'New gate',
  'Keine Phasen oder Gates': 'No phases or gates',
  'Strukturiere das Projekt in Phasen und lege Freigabe-Gates mit messbaren Kriterien fest.':
    'Structure the project into phases and define approval gates with measurable criteria.',
  'Phasenverlauf': 'Phase progression',
  'Gates ohne Phase': 'Gates without a phase',
  'Phase bearbeiten': 'Edit phase',
  'Reihenfolge': 'Order',
  'Budget geplant': 'Planned budget',
  'Budget Ist': 'Actual budget',
  'Phase löschen?': 'Delete phase?',
  '„{name}“ wird gelöscht. Aufgaben und Gates bleiben erhalten, verlieren aber die Zuordnung.':
    '“{name}” will be deleted. Tasks and gates are kept but lose their assignment.',
  'Gate {code} bearbeiten': 'Edit gate {code}',
  'Code': 'Code',
  'z. B. G1': 'e.g. G1',
  'Kriterien': 'Criteria',
  'Ein Kriterium pro Zeile; Zielwert optional nach „|“, z. B. „Erfolgsquote | ≥ 90 %“':
    'One criterion per line; optional target after “|”, e.g. “Success rate | ≥ 90 %”',
  'Gate löschen?': 'Delete gate?',
  'Gate {code} und seine Kriterien werden gelöscht.': 'Gate {code} and its criteria will be deleted.',
  'bestanden': 'passed',
  'mit Ausnahme freigeben': 'approve with waiver',
  'nicht bestanden': 'failed',
  'Die Entscheidung wird mit dem Kriterienstand im Entscheidungsprotokoll festgehalten.':
    'The decision is recorded in the decision log together with the state of the criteria.',
  'Begründung der Ausnahme (Pflicht)': 'Reason for the waiver (required)',
  'Begründung': 'Rationale',
  'Entscheidung festhalten': 'Record decision',
  'Gate {code} entschieden': 'Gate {code} decided',
  'Im Entscheidungsprotokoll dokumentiert.': 'Recorded in the decision log.',

  // ------------------------------------------------------------ schedule and calendar
  'Noch kein Zeitplan': 'No schedule yet',
  'Phasen mit Zeitraum, Gates mit Fälligkeit und Aufgaben mit Termin erscheinen hier auf einer Zeitachse.':
    'Phases with a date range, gates with a due date and tasks with a deadline appear here on a timeline.',
  'Aufgaben mit Termin': 'Tasks with a deadline',
  'Zeitachse {from} – {to} · heute {today}': 'Timeline {from} – {to} · today {today}',
  'Termine': 'Dates',
  'Fällige Aufgaben, Gates, Risikoprüfungen und Phasenenden über alle Projekte.':
    'Due tasks, gates, risk reviews and phase ends across all projects.',
  'Vorheriger Monat': 'Previous month',
  'Heute': 'Today',
  'Nächster Monat': 'Next month',
  'Abonnieren (ICS)': 'Subscribe (ICS)',
  '{n} Termine': '{n} items',
  'Risikoprüfung': 'Risk review',
  'Phasenende': 'Phase end',
  'Kalender-Abo': 'Calendar subscription',
  'Kalender abonnieren': 'Subscribe to calendar',
  'Diese Adresse in Outlook, Google Kalender oder Apple Kalender als Internetkalender hinzufügen. Sie enthält ein nur-lesendes Token, das du unter Einstellungen jederzeit widerrufen kannst. Der Link wird nur jetzt angezeigt.':
    'Add this address to Outlook, Google Calendar or Apple Calendar as an internet calendar. It contains a read-only token that you can revoke under Settings at any time. The link is shown only now.',
  'Abo-Adresse': 'Subscription address',
  'Mo': 'Mon',
  'Di': 'Tue',
  'Mi': 'Wed',
  'Do': 'Thu',
  'Fr': 'Fri',
  'Sa': 'Sat',
  'So': 'Sun',

  // ------------------------------------------------------------ contacts and companies
  'Beziehungen': 'Relations',
  'Menschen hinter Kunden, Lieferanten und Projekten – mit Kernzeiten und Abwesenheiten.':
    'The people behind clients, suppliers and projects – with core hours and absences.',
  'Kontakte durchsuchen': 'Search contacts',
  'Neuer Kontakt': 'New contact',
  'Position': 'Position',
  'Telefon': 'Phone',
  'Kernzeiten': 'Core hours',
  'Noch keine Kontakte': 'No contacts yet',
  'Kontakte lassen sich Projekten als Stakeholder zuordnen, Aufgaben delegieren und Risiken zuweisen.':
    'Contacts can be assigned to projects as stakeholders, receive delegated tasks and own risks.',
  'Dr., Mag., …': 'Dr., Prof., …',
  'Vorname': 'First name',
  'Nachname': 'Last name',
  'Keine Firma': 'No company',
  'Kernarbeitszeiten': 'Core working hours',
  '{day} von': '{day} from',
  '{day} bis': '{day} to',
  'Abwesenheiten': 'Absences',
  'Urlaub': 'Vacation',
  'Krank': 'Sick',
  'Sonstiges': 'Other',
  'Kontakt gespeichert': 'Contact saved',
  'Kontakt löschen?': 'Delete contact?',
  '{name} wird gelöscht und aus allen Projekten entfernt.': '{name} will be deleted and removed from all projects.',
  'Kunden, Lieferanten und Partner.': 'Clients, suppliers and partners.',
  'Firmen durchsuchen': 'Search companies',
  'Neue Firma': 'New company',
  'Ort': 'City',
  'Web': 'Web',
  'Noch keine Firmen': 'No companies yet',
  'Firmen werden Projekten als Kunde zugeordnet und Kontakten als Arbeitgeber.':
    'Companies are assigned to projects as clients and to contacts as employers.',
  'Website': 'Website',
  'Straße': 'Street',
  'PLZ': 'Postal code',
  'Land': 'Country',
  'UID-Nummer': 'VAT ID',
  'Firma gespeichert': 'Company saved',
  'Firma löschen?': 'Delete company?',
  '{name} wird gelöscht. Projekte und Kontakte verlieren die Zuordnung.': '{name} will be deleted. Projects and contacts lose the assignment.',

  // ------------------------------------------------------------ comments and links
  'Kommentare ({n})': 'Comments ({n})',
  'bearbeitet': 'edited',
  'Kommentar löschen': 'Delete comment',
  'Noch keine Kommentare.': 'No comments yet.',
  'Neuer Kommentar': 'New comment',
  'Kommentar schreiben … mit „Erwähnen“ Personen benachrichtigen': 'Write a comment … use “Mention” to notify people',
  'Person erwähnen': 'Mention a person',
  'Erwähnen …': 'Mention …',
  'Kommentieren': 'Comment',
  'Andere': 'Other',
  'Verknüpfungen': 'Links',
  'Verknüpfung {id} entfernen': 'Remove link {id}',
  'Keine Verknüpfungen zu Beads, Jira, GitHub oder AgentOps.': 'No links to Beads, Jira, GitHub or AgentOps.',
  'ID, z. B. pma-42': 'ID, e.g. pma-42',
  'Externe ID': 'External ID',
  'Verknüpfen': 'Link',

  // ------------------------------------------------------------ settings
  'Einstellungen & Schnittstellen': 'Settings & integrations',
  'Profil, Mitglieder, API-Zugänge sowie aus- und eingehende Verbindungen zu Purple Suite, Jira, GitHub, Teams oder eigenen Systemen.':
    'Profile, members, API access and outgoing and incoming connections to the Purple suite, Jira, GitHub, Teams or your own systems.',
  'Passwort ändern': 'Change password',
  'Sprache': 'Language',
  'Profil bearbeiten': 'Edit profile',
  'Gilt für Oberfläche, Benachrichtigungen und Berichte.': 'Applies to the interface, notifications and reports.',
  'Profil gespeichert': 'Profile saved',
  'Ungelesene per E-Mail (nach 2 Minuten, gebündelt)': 'Unread ones by email (after 2 minutes, bundled)',
  'Nur in der App': 'In the app only',
  'Erwähnungen, Zuweisungen und Kommentare zu deinen Aufgaben. E-Mails gehen nur raus, wenn der Server Mail versenden kann.':
    'Mentions, assignments and comments on your tasks. Emails are only sent when the server can send mail.',
  'Andere angemeldete Geräte werden danach abgemeldet.': 'Other signed-in devices will be signed out.',
  'Aktuelles Passwort': 'Current password',
  'Neues Passwort': 'New password',
  'Passwort geändert': 'Password changed',
  'Tarif {plan}': 'Plan {plan}',
  'Rolle von {name}': 'Role of {name}',
  'API-Tokens': 'API tokens',
  'Token erstellen': 'Create token',
  'Tokens authentifizieren Skripte, Agenten und Integrationen per {header}. Die vollständige API ist unter {link} beschrieben.':
    'Tokens authenticate scripts, agents and integrations via {header}. The full API is described at {link}.',
  'Widerrufen': 'Revoke',
  'Keine Tokens': 'No tokens',
  'Erstelle ein Token, um PMA aus AgentOps, Skripten oder anderen Werkzeugen anzusprechen.':
    'Create a token to call PMA from AgentOps, scripts or other tools.',
  'Ausgehende Verbindungen': 'Outgoing connections',
  'Verbindung hinzufügen': 'Add connection',
  'Ereignisse gehen an Teams, Discord, Slack, Jira oder eigene Systeme – mit Vorlagen oder frei definiertem JSON. Zustellung transaktional mit bis zu 8 Wiederholungen; PMA-JSON ist signiert ({signature}).':
    'Events go to Teams, Discord, Slack, Jira or your own systems – from templates or with custom JSON. Delivery is transactional with up to 8 retries; PMA JSON is signed ({signature}).',
  '{n} zugestellt': '{n} delivered',
  '{n} fehlgeschlagen': '{n} failed',
  'Testen': 'Test',
  'Protokoll': 'Log',
  'Keine Webhooks': 'No webhooks',
  'Schicke Ereignisse wie task.created oder gate.decided an Slack-Brücken, AgentOps oder eigene Dienste.':
    'Send events such as task.created or gate.decided to Slack bridges, AgentOps or your own services.',
  'Angemeldet als {email} · Rolle {role}': 'Signed in as {email} · role {role}',
  'Rolle geändert': 'Role changed',
  'Person einladen': 'Invite person',
  'Offene Einladungen': 'Open invitations',
  'abgelaufen': 'expired',
  'gültig bis {date}': 'valid until {date}',
  'per E-Mail verschickt': 'sent by email',
  'Einladungen gelten 14 Tage und nur für die eingeladene E-Mail-Adresse. Gäste sehen nur Projekte, die im Projektüberblick für sie freigegeben sind.':
    'Invitations are valid for 14 days and only for the invited email address. Guests only see projects shared with them in the project overview.',
  'PMA schickt den Einladungslink per E-Mail; du kannst ihn zusätzlich kopieren.': 'PMA sends the invitation link by email; you can also copy it.',
  'Ausgehende E-Mail ist noch nicht eingerichtet: Du bekommst den Einladungslink zum Kopieren und gibst ihn selbst weiter.':
    'Outgoing email is not set up yet: you get the invitation link to copy and share it yourself.',
  'Einladen': 'Invite',
  'Einladung erstellt': 'Invitation created',
  'Einladungslink': 'Invitation link',
  'Die E-Mail an {email} ist unterwegs. Der Link wird nur jetzt angezeigt.': 'The email to {email} is on its way. The link is shown only now.',
  'Die E-Mail konnte nicht verschickt werden. Gib den Link selbst weiter; er wird nur jetzt angezeigt.':
    'The email could not be sent. Share the link yourself; it is shown only now.',
  'Gib den Link an {email} weiter; er wird nur jetzt angezeigt.': 'Share the link with {email}; it is shown only now.',
  'Einladung widerrufen?': 'Revoke invitation?',
  'Der Link funktioniert danach nicht mehr.': 'The link stops working.',
  // Invitation page
  'Einladung nicht gültig': 'Invitation not valid',
  'Der Link ist abgelaufen, wurde widerrufen oder schon verwendet. Bitte die einladende Person um einen neuen Link.':
    'The link has expired, was revoked or has already been used. Ask the person who invited you for a new link.',
  'Zur Anmeldung': 'Go to sign-in',
  'Einladung zu {organization}': 'Invitation to {organization}',
  '{name} lädt dich als {role} ein.': '{name} invites you as {role}.',
  'Du bist als {role} eingeladen.': 'You are invited as {role}.',
  'Für {email} · gültig bis {date}': 'For {email} · valid until {date}',
  'Einladung annehmen': 'Accept invitation',
  'Du bist als {email} angemeldet.': 'You are signed in as {email}.',
  'Beitreten': 'Join',
  'Anderes Konto angemeldet': 'Signed in with another account',
  'Du bist als {current} angemeldet, die Einladung gilt aber für {email}.': 'You are signed in as {current}, but the invitation is for {email}.',
  'Abmelden und fortfahren': 'Sign out and continue',
  'Mit deinem Konto anmelden': 'Sign in with your account',
  'Für {email} gibt es schon ein Konto. Melde dich an, danach kannst du beitreten.': 'An account for {email} already exists. Sign in, then you can join.',
  'Konto anlegen und beitreten': 'Create account and join',
  'Rolle': 'Role',
  'Gäste: nur freigegebene Projekte, nur lesen': 'Guests: shared projects only, read-only',
  'Mitglied entfernen?': 'Remove member?',
  'Die Person verliert sofort den Zugriff auf diese Organisation.': 'The person loses access to this organisation immediately.',
  'API-Token erstellen': 'Create API token',
  'z. B. AgentOps': 'e.g. AgentOps',
  'Rechte': 'Permissions',
  'Lesen und schreiben': 'Read and write',
  'Nur lesen': 'Read only',
  'Gültig (Tage)': 'Valid (days)',
  'Leer = unbefristet': 'Empty = no expiry',
  'Token erstellt': 'Token created',
  'Token': 'Token',
  'Das Token wird nur jetzt angezeigt. Bewahre es sicher auf.': 'The token is shown only now. Keep it safe.',
  'Token widerrufen?': 'Revoke token?',
  'Integrationen mit diesem Token verlieren sofort den Zugriff.': 'Integrations using this token lose access immediately.',
  'Test-Ereignis eingereiht': 'Test event queued',
  'Zustellung erfolgt in wenigen Sekunden.': 'Delivery follows within a few seconds.',
  'Webhook löschen?': 'Delete webhook?',
  'Offene Zustellungen werden verworfen.': 'Pending deliveries are discarded.',
  'Zustellprotokoll': 'Delivery log',
  'Ereignis': 'Event',
  'Versuche': 'Attempts',
  'Zeit': 'Time',
  'Noch keine Zustellungen.': 'No deliveries yet.',

  // ------------------------------------------------------------ connectors
  '{label} ist kein gültiges JSON': '{label} is not valid JSON',
  'PMA-JSON, signiert (eigene Dienste)': 'PMA JSON, signed (own services)',
  'Slack (Incoming Webhook)': 'Slack (incoming webhook)',
  'Ausgehende Verbindung': 'Outgoing connection',
  'Ereignisse aus PMA an ein anderes System senden. Vorlagen füllen Methode, Header und JSON-Körper vor; Platzhalter wie {{summary}} oder {{data.after.title}} werden beim Senden ersetzt.':
    'Send events from PMA to another system. Templates prefill method, headers and JSON body; placeholders such as {{summary}} or {{data.after.title}} are replaced when sending.',
  'z. B. Teams-Kanal Projekt': 'e.g. Teams project channel',
  'Methode': 'Method',
  'Ziel-URL': 'Target URL',
  'Header (eine Zeile pro Header: Name: Wert)': 'Headers (one per line: Name: value)',
  'Werte werden gespeichert, aber nie wieder angezeigt.': 'Values are stored but never shown again.',
  'JSON-Körper (Vorlage)': 'JSON body (template)',
  'Ereignisse': 'Events',
  'Alle Ereignisse (*)': 'All events (*)',
  'Incoming-Webhook-URL aus der Slack-App.': 'Incoming webhook URL from the Slack app.',
  'Signierter JSON-Körper; Prüfung per x-pma-signature.': 'Signed JSON body; verify via x-pma-signature.',
  'Der JSON-Körper': 'The JSON body',
  'Webhook angelegt': 'Webhook created',
  'Signatur-Secret': 'Signing secret',
  'Mit diesem Secret prüft der Empfänger die Signatur. Es wird nur jetzt angezeigt.':
    'The receiver verifies the signature with this secret. It is shown only now.',
  'Verbindung angelegt': 'Connection created',
  'Mit „Testen“ prüfst du sie.': 'Use “Test” to check it.',
  'Aufgabe anlegen/aktualisieren': 'Create/update task',
  'Risiko anlegen/aktualisieren': 'Create/update risk',
  'Notiz anlegen': 'Create note',
  'Kennzahl-Messwert erfassen': 'Record KPI measurement',
  'Eingehender Connector': 'Incoming connector',
  'Ein anderes System schickt Ereignisse an eine eigene URL; das Mapping übersetzt sie in PMA-Einträge. Wiederholte Lieferungen aktualisieren denselben Eintrag (über die externe ID).':
    'Another system sends events to a dedicated URL; the mapping turns them into PMA records. Repeated deliveries update the same record (via the external ID).',
  'Zielprojekt': 'Target project',
  'Aktion': 'Action',
  'System-Kürzel': 'System key',
  'z. B. github, jira, alerts': 'e.g. github, jira, alerts',
  'Betriebsart': 'Mode',
  'Empfang: das System sendet an eine PMA-URL': 'Push: the system sends to a PMA URL',
  'Abruf: PMA holt regelmäßig eine JSON-Quelle': 'Pull: PMA fetches a JSON source periodically',
  'Quelle (URL)': 'Source (URL)',
  'Intervall (Minuten)': 'Interval (minutes)',
  'Pfad zur Liste': 'Path to the list',
  'z. B. issues; leer = die Antwort selbst': 'e.g. issues; empty = the response itself',
  'Header (Name: Wert je Zeile)': 'Headers (Name: value per line)',
  'Mapping (JSON)': 'Mapping (JSON)',
  'fields: Zielfelder mit Platzhaltern aus dem eingehenden JSON; external_id macht Lieferungen idempotent; when filtert; status.map übersetzt Zustände.':
    'fields: target fields with placeholders from the incoming JSON; external_id makes deliveries idempotent; when filters; status.map translates states.',
  'Connector anlegen': 'Create connector',
  'Das Mapping': 'The mapping',
  'Abruf-Connector angelegt': 'Pull connector created',
  'Mit „Jetzt abrufen“ testest du die Quelle sofort.': 'Use “Pull now” to test the source right away.',
  'Connector angelegt': 'Connector created',
  'Endpunkt': 'Endpoint',
  'Token (Header X-PMA-Token)': 'Token (header X-PMA-Token)',
  'URL mit Token (für Dienste ohne eigene Header)': 'URL with token (for services without custom headers)',
  'Das Token wird nur jetzt angezeigt. Dienste senden JSON per POST an den Endpunkt.':
    'The token is shown only now. Services POST JSON to the endpoint.',
  'Vorschau: {name}': 'Preview: {name}',
  'Beispiel-JSON einfügen, wie es das andere System sendet. Es wird nichts gespeichert.':
    'Paste sample JSON as the other system sends it. Nothing is stored.',
  'Eingehendes JSON': 'Incoming JSON',
  'Mapping anwenden': 'Apply mapping',
  'Das Beispiel': 'The sample',
  'Eingehende Connectoren': 'Incoming connectors',
  'Connector hinzufügen': 'Add connector',
  'Andere Systeme legen Aufgaben, Risiken, Notizen oder Messwerte an – per {push} über eine eigene URL (GitHub, GitLab, Monitoring, Skripte) oder per {pull} einer JSON-Quelle im Intervall (Jira-Suche, Statusseiten, eigene APIs).':
    'Other systems create tasks, risks, notes or measurements – by {push} to a dedicated URL (GitHub, GitLab, monitoring, scripts) or by {pull} of a JSON source on an interval (Jira search, status pages, your own APIs).',
  'Empfang': 'push',
  'Abruf': 'pull',
  'Abruf alle {n} min': 'pull every {n} min',
  '{n} empfangen': '{n} received',
  'Jetzt abrufen': 'Pull now',
  'Vorschau': 'Preview',
  'Keine eingehenden Connectoren': 'No incoming connectors',
  'Beginne mit einer Vorlage, z. B. GitHub Issues → Aufgaben.': 'Start from a template, e.g. GitHub issues → tasks.',
  'Abruf mit Fehler': 'Pull finished with an error',
  'Abruf abgeschlossen': 'Pull finished',
  '{items} Einträge: {created} neu, {updated} aktualisiert, {skipped} übersprungen, {failed} fehlgeschlagen':
    '{items} items: {created} new, {updated} updated, {skipped} skipped, {failed} failed',
  'Connector löschen?': 'Delete connector?',
  'Die URL funktioniert danach nicht mehr. Bereits angelegte Einträge bleiben erhalten.':
    'The URL stops working. Records already created are kept.',

  // ------------------------------------------------------------ server vocabulary shown via t(value)
  // Connector presets (src/server/connectors/presets.ts).
  'Microsoft Teams (Workflows-Webhook)': 'Microsoft Teams (Workflows webhook)',
  'URL aus einem Teams-Workflow „Beim Empfang einer Webhookanforderung posten“.':
    'URL from a Teams workflow “Post to a channel when a webhook request is received”.',
  'Discord': 'Discord',
  'Kanal-Einstellungen → Integrationen → Webhooks.': 'Channel settings → Integrations → Webhooks.',
  'Mattermost / Rocket.Chat': 'Mattermost / Rocket.Chat',
  'Eingehender Webhook des Kanals.': 'Incoming webhook of the channel.',
  'Jira: Vorgang aus neuer Aufgabe': 'Jira: issue from new task',
  'URL: https://<site>.atlassian.net/rest/api/2/issue. Jira-Projektschlüssel und Zugang anpassen.':
    'URL: https://<site>.atlassian.net/rest/api/2/issue. Adjust the Jira project key and credentials.',
  'Eigenes System (JSON)': 'Own system (JSON)',
  'Platzhalter: event.*, project.*, actor.*, entity.*, data.*, summary.': 'Placeholders: event.*, project.*, actor.*, entity.*, data.*, summary.',
  'GitHub Issues → Aufgaben': 'GitHub issues → tasks',
  'Repository → Settings → Webhooks, Content type application/json, Ereignis „Issues“. Token als ?token=… an die URL hängen.':
    'Repository → Settings → Webhooks, content type application/json, event “Issues”. Append the token as ?token=… to the URL.',
  'GitLab Issues → Aufgaben': 'GitLab issues → tasks',
  'GitLab: Secret Token-Feld mit dem Connector-Token füllen (Header X-Gitlab-Token).':
    'GitLab: put the connector token into the Secret token field (header X-Gitlab-Token).',
  'Beliebiges System → Notiz': 'Any system → note',
  'POST {"title": "…", "text": "…"} mit Header X-PMA-Token.': 'POST {"title": "…", "text": "…"} with header X-PMA-Token.',
  'Monitoring/Alarm → Risiko': 'Monitoring/alert → risk',
  'Idempotent über id; severity 1–5.': 'Idempotent via id; severity 1–5.',
  'Jira-Suche → Aufgaben (Abruf)': 'Jira search → tasks (pull)',
  'Jira Cloud REST-Suche; JQL, Site und Zugang anpassen. Holt geänderte Vorgänge alle 15 Minuten.':
    'Jira Cloud REST search; adjust JQL, site and credentials. Fetches changed issues every 15 minutes.',
  'Statusseite (Atlassian Statuspage) → Risiken (Abruf)': 'Status page (Atlassian Statuspage) → risks (pull)',
  'Offene Störungen eines Dienstleisters als Risiken im Projekt.': 'Open incidents of a provider as risks in the project.',
  'Messwert → Kennzahl': 'Measurement → KPI',
  'z. B. aus AgentOps, Quality Console oder einem Cronjob: {"value": 91.5, "date": "2026-09-27"}.':
    'e.g. from AgentOps, Quality Console or a cron job: {"value": 91.5, "date": "2026-09-27"}.',

  // ------------------------------------------------------------ sync status
  'In Ordnung': 'Healthy',
  'Mit Fehlern': 'With errors',
  'Fehlgeschlagen': 'Failed',
  'Veraltet': 'Stale',
  'Purple Suite (Beads, Quality Console)': 'Purple suite (Beads, Quality Console)',
  '{n} Projekte angelegt': '{n} projects created',
  '{n} Aufgaben angelegt': '{n} tasks created',
  '{n} aktualisiert': '{n} updated',
  '{n} unverändert': '{n} unchanged',
  '{n} Messwerte': '{n} measurements',
  '{n} Testfehler geöffnet': '{n} test failures opened',
  '{n} Testfehler behoben': '{n} test failures resolved',
  '{n} s': '{n} s',
  'Synchronisationen': 'Syncs',
  'Externe Abgleiche melden jeden Lauf über {endpoint}. Eine Quelle gilt als veraltet, wenn sie doppelt so lange wie erwartet nichts gemeldet hat.':
    'External syncs report every run via {endpoint}. A source is stale when it has not reported for twice its expected interval.',
  'Letzter Lauf {when}': 'Last run {when}',
  'erwartet alle {n} min': 'expected every {n} min',
  '… und {n} weitere': '… and {n} more',
  'Zuletzt fehlerfrei: {when}': 'Last without errors: {when}',
  '{n} Fehlläufe in 24 h': '{n} failed runs in 24 h',
  'Noch keine Synchronisation gemeldet': 'No sync reported yet',
  'Der Purple-Suite-Abgleich meldet sich nach seinem nächsten Lauf hier.': 'The Purple suite sync appears here after its next run.',
  '{source}: seit {when} kein Lauf': '{source}: no run since {when}',
  '{source}: letzter Lauf fehlgeschlagen': '{source}: last run failed',
  'Details in den Einstellungen': 'Details in the settings',
  'Synchronisationsstatus derzeit nicht verfügbar.': 'Sync status is currently unavailable.',

  // ------------------------------------------------------------ guest sections
  'Für Gäste sichtbar': 'Visible to guests',
  'Projektkopf, Überblick und Verlauf sind immer sichtbar und zeigen nur freigegebene Bereiche. Interne Notizen und Risiken bleiben standardmäßig verborgen.':
    'Project header, overview and activity are always visible and show only shared sections. Internal notes and risks stay hidden by default.',
  'Sichtbarkeit für Gäste gespeichert': 'Guest visibility saved',

  // ------------------------------------------------------------ invitation projects
  'Projekte freigeben': 'Share projects',
  'Noch keine Projekte.': 'No projects yet.',
  'Der Gast sieht diese Projekte direkt nach der Annahme – welche Bereiche, legst du je Projekt unter „Freigabe für Gäste“ fest.':
    'The guest sees these projects right after accepting; choose the visible sections per project under “Guest access”.',
  'Projekte {keys}': 'Projects {keys}',

  // ------------------------------------------------------------ client portal
  '{n} von {total}': '{n} of {total}',
  'Nächster Meilenstein': 'Next milestone',
  'Alle Meilensteine entschieden': 'All milestones decided',
  'Projekt öffnen': 'Open project',
  'Kundenportal': 'Client portal',
  'Der aktuelle Stand deiner Projekte – Phasen, Meilensteine, Entscheidungen und Kennzahlen.':
    'The current state of your projects – phases, milestones, decisions and KPIs.',
  'Noch kein Projekt freigegeben': 'No project shared yet',
  'Sobald das Projektteam ein Projekt für dich freigibt, erscheint es hier.': 'As soon as the project team shares a project with you, it appears here.',

  // ------------------------------------------------------------ guest comments
  'Kommentar schreiben …': 'Write a comment …',
  // Password reset
  'Passwort vergessen?': 'Forgot your password?',
  'Passwort vergessen': 'Forgot your password',
  'Gib die E-Mail-Adresse deines Kontos ein. Du bekommst einen Link, mit dem du ein neues Passwort festlegst.':
    'Enter the email address of your account. You will get a link to set a new password.',
  'Link senden': 'Send link',
  'Zurück zur Anmeldung': 'Back to sign-in',
  'Schau in dein Postfach': 'Check your inbox',
  'Wenn es für {email} ein Konto gibt, ist ein Link unterwegs. Er ist eine Stunde gültig.':
    'If there is an account for {email}, a link is on its way. It is valid for one hour.',
  'Nichts angekommen? Prüfe den Spam-Ordner oder fordere nach ein paar Minuten einen neuen Link an.':
    'Nothing arrived? Check your spam folder or request a new link after a few minutes.',
  'Zurücksetzen nicht verfügbar': 'Reset not available',
  'Diese Installation verschickt keine E-Mails. Bitte wende dich an die Person, die PMA für dich betreibt.':
    'This installation does not send email. Please contact the person who runs PMA for you.',
  'Link nicht gültig': 'Link not valid',
  'Der Link ist abgelaufen oder wurde schon verwendet. Fordere einen neuen an.': 'The link has expired or was already used. Request a new one.',
  'Neuen Link anfordern': 'Request a new link',
  'Neues Passwort festlegen': 'Set a new password',
  'Für {email}. Danach bist du auf allen anderen Geräten abgemeldet.': 'For {email}. Afterwards you are signed out on all other devices.',
  'Passwort wiederholen': 'Repeat password',
  'Passwort speichern': 'Save password',
  'Die Passwörter stimmen nicht überein.': 'The passwords do not match.',
  // ------------------------------------------------------------ site chrome, legal, feedback
  'Beta': 'Beta',
  'Hinweis zur Vorabversion': 'Pre-release notice',
  'Vorabversion.': 'Pre-release.',
  'Funktionen können sich ändern und Daten können verloren gehen. Bitte keine vertraulichen Daten ablegen.':
    'Features may change and data may be lost. Please do not store confidential data.',
  'Feedback geben': 'Give feedback',
  'Hinweis ausblenden': 'Hide notice',
  'Rechtliches und Kontakt': 'Legal and contact',
  'Impressum': 'Legal notice',
  'Datenschutz': 'Privacy',
  'Feedback': 'Feedback',
  'Version {version}': 'Version {version}',
  'Wien': 'Vienna',
  'Idee': 'Idea',
  'Frage': 'Question',
  'Was funktioniert nicht, was fehlt, was gefällt dir? Die Nachricht geht per E-Mail an das PMA-Team; die Antwort kommt an deine Adresse.':
    'What does not work, what is missing, what do you like? The message is emailed to the PMA team; the answer comes to your address.',
  'Senden': 'Send',
  'Nachricht': 'Message',
  'Mitgesendet werden dein Name, deine E-Mail-Adresse, die aktuelle Seite ({page}) und die Version.':
    'Your name, email address, the current page ({page}) and the version are sent along.',
  'Danke für dein Feedback!': 'Thank you for your feedback!',
  'Die Nachricht ist unterwegs.': 'The message is on its way.',
  'Zurück zur App': 'Back to the app',
  'Für diese Installation ist noch kein Text hinterlegt.': 'No text has been provided for this installation yet.',
  // ------------------------------------------------------------ own data, account deletion
  'Gelöschte Person': 'Deleted person',
  'Deine Daten': 'Your data',
  'Alles, was zu deinem Konto gehört, als JSON-Datei. Projektinhalte exportierst du pro Projekt als CSV.':
    'Everything tied to your account as a JSON file. Export project content per project as CSV.',
  'Meine Daten herunterladen': 'Download my data',
  'Konto löschen': 'Delete account',
  'Du bist die einzige Inhaberin bzw. der einzige Inhaber von: {orgs}. Übertrage dort zuerst die Inhaberschaft an ein anderes Mitglied (Einstellungen → Mitglieder → Rolle „Inhaber“), dann kannst du dein Konto löschen.':
    'You are the only owner of: {orgs}. Hand ownership over to another member there first (Settings → members → role “Owner”), then you can delete your account.',
  'Verstanden': 'Understood',
  'Konto endgültig löschen': 'Delete account permanently',
  'Gelöscht werden samt allen Inhalten: {orgs}.': 'Deleted with all their content: {orgs}.',
  'Du verlässt: {orgs}. Deine Beiträge dort bleiben erhalten und erscheinen als „Gelöschte Person“.':
    'You leave: {orgs}. Your contributions there remain and show as “Deleted person”.',
  'Deine API-Tokens und von dir angelegten Konnektoren werden gelöscht. Sicherungen enthalten die Daten noch bis zu 30 Tage. Das lässt sich nicht rückgängig machen.':
    'Your API tokens and the connectors you created are deleted. Backups still contain the data for up to 30 days. This cannot be undone.',
  'Passwort zur Bestätigung': 'Password to confirm',
};
